import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';

type GenerateDataExportOptions = {
  config_id: string;
  include_sensitive_fields?: boolean;
};

type GenerateDataExportVariables = {
  clientId: string;
  resource: string;
  values: GenerateDataExportOptions;
};

export function useGenerateDataExport() {
  const queryClient = useQueryClient();

  const {
    mutateAsync,
    error,
    isPending: isLoading,
    reset,
  } = useMutation<{ data: unknown }, Error, GenerateDataExportVariables>({
    mutationFn: ({ resource, values }) => api.post(resource, values),
    onSuccess: (_data, { clientId }) => {
      queryClient.invalidateQueries({
        queryKey: ['clients', clientId, 'data-exports'],
      });
    },
  });

  const generateClientDataExport = async (clientId: string, values: GenerateDataExportOptions) => {
    return mutateAsync({
      clientId,
      resource: `clients/${clientId}/data-exports`,
      values: {
        config_id: values.config_id,
        include_sensitive_fields: values.include_sensitive_fields,
      },
    });
  };

  const generateFamilyDataExport = async (
    familyId: string,
    clientId: string,
    values: GenerateDataExportOptions,
  ) => {
    return mutateAsync({
      clientId,
      resource: `families/${familyId}/data-exports`,
      values: {
        config_id: values.config_id,
        include_sensitive_fields: values.include_sensitive_fields,
      },
    });
  };

  const generateDataExport = async (
    type: 'client' | 'family',
    id: string,
    clientId: string,
    values: GenerateDataExportOptions,
  ) => {
    if (type === 'client') {
      return generateClientDataExport(id, values);
    } else {
      return generateFamilyDataExport(id, clientId, values);
    }
  };

  return {
    generateDataExport,
    generateClientDataExport,
    generateFamilyDataExport,
    error,
    isLoading,
    reset,
  };
}
